import { Grid, Typography } from '@mui/material';
import { SubmitButton } from '@tassat/button';
import { CurrencyField } from '@tassat/formik-input';
import { PriceRecord } from '@tassat/fred-definitions';
import {
    assetConversionHelper,
    formatAsAsset,
    formatAsAssetNoSign,
    stripPrefix
} from '@tassat/utility';
import { Formik } from 'formik';
import React from 'react';
import ReactGA from 'react-ga4';

import { useAppSelector } from 'Reducers/store/hooks';
import { getPrices } from 'Selectors/common';

import useStyles from './styles';
import { validateAddOn } from './submission';
import {
    IAddOnFormErrors,
    IAddOnFormProps,
    IAddOnFormValues,
    IAddOnFormikProps
} from './types';

const FormContents = (props: IAddOnFormProps) => {
    const classes = useStyles();
    const {
        formProps,
        errors,
        values,
        isSubmitting,
        isValid,
        setFieldValue,
        submitForm,
        setFieldTouched
    } = props;

    const {
        pledge,
        assetPair,
        accountPositionBalance
    } = formProps;

    const securityObject = assetPair?.security;
    const ccyObject = assetPair?.currency;
    const securityAvailableBalance = accountPositionBalance?.[0]?.security[securityObject?.AssetId ?? ''];

    const [ disableFiatField, setDisabledFiatField ] = React.useState(false);
    const [ disableAssetField, setDisableAssetField ] = React.useState(false);
    const [ isFiatAmountBeingUpdated, setIsFiatAmountBeingUpdated ] = React.useState(false);
    const [ isAssetAmountBeingUpdated, setIsAssetAmountBeingUpdated ] = React.useState(false);
    const realTimePriceList = useAppSelector(getPrices);

    const realTimePriceRecord = React.useMemo(() => {
        if (!realTimePriceList || !securityObject) return {} as PriceRecord;
        const matched = Object.keys(realTimePriceList)
            .filter(key => realTimePriceList[key]?.AssetId === securityObject.AssetId)
            .map(key => realTimePriceList[key]);

        return matched.length ? matched[0] : undefined;
    }, [ realTimePriceList, securityObject ]);

    React.useEffect(() => {
        if (isAssetAmountBeingUpdated) return;

        const realFiatAmount = stripPrefix(values.FiatAmount, '$');

        if (realFiatAmount === '.') {
            setFieldValue('FiatAmount', '0.');
            setFieldValue('AssetAmount', '0.');
        }

        if (disableAssetField && realFiatAmount === '') {
            setFieldValue('AssetAmount', '');
            setIsFiatAmountBeingUpdated(false);
            setDisableAssetField(false);
        }

        if (realFiatAmount !== '') {
            setDisableAssetField(true);
            setIsFiatAmountBeingUpdated(true);
            const delayDebounceFn = setTimeout(() => {
                if (securityObject) {
                    setFieldValue('AssetAmount', (assetConversionHelper(values.FiatAmount, realTimePriceRecord?.Price, securityObject)));
                }
                setFieldTouched('AssetAmount');
            }, 500);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [values.FiatAmount]);

    React.useEffect(() => {
        if (isFiatAmountBeingUpdated) return;

        const realAssetAmount = stripPrefix(values.AssetAmount, securityObject?.Ticker ?? '');

        if (realAssetAmount === '.') {
            setFieldValue('FiatAmount', '0.');
            setFieldValue('AssetAmount', '0.');
        }

        if (disableFiatField && realAssetAmount.length <= 1) {
            setFieldValue('FiatAmount', '');
            setIsAssetAmountBeingUpdated(false);
            setDisabledFiatField(false);
        }

        if (realAssetAmount.length > 1) {
            setDisabledFiatField(true);
            setIsAssetAmountBeingUpdated(true);
            const delayDebounceFn = setTimeout(() => {
                if (ccyObject) {
                    setFieldValue('FiatAmount', assetConversionHelper(values.AssetAmount, realTimePriceRecord?.Price, ccyObject));
                }
            }, 500);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [values.AssetAmount]);

    React.useEffect(() => {
        setFieldValue('isFiatAmount', disableAssetField);
    }, [ disableAssetField, setFieldValue ]);

    const securityBalanceInFiat = securityAvailableBalance && realTimePriceRecord ?
        Number(securityAvailableBalance.availableBalance) * Number(realTimePriceRecord.Price)
        : 0;

    const availableBalanceDisplay = securityAvailableBalance ?
        `${formatAsAssetNoSign(securityAvailableBalance.availableBalance, securityObject, true)} ≈ ${formatAsAsset(securityBalanceInFiat, ccyObject, 'symbol')}`
        : '--';

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={2}
            >
                <Grid
                    className={classes.fieldContainer}
                    item
                >
                    <Typography
                        className={classes.sectionTitle}
                        variant='h5'
                    >
                        <strong>Asset Amount</strong>
                    </Typography>
                    <CurrencyField
                        currencyDecimalPoint={2}
                        disabled={disableFiatField}
                        errors={errors}
                        fieldSize='large'
                        label=''
                        name='FiatAmount'
                        placeholder='USD'
                    />
                    <Typography
                        className={classes.assetEquivalency}
                        variant='h2'
                    >
                        <strong>≈</strong>
                    </Typography>
                    <CurrencyField
                        currencyDecimalPoint={6}
                        currencySymbol={securityObject?.Ticker}
                        disabled={disableAssetField}
                        errors={errors}
                        fieldSize='large'
                        label=''
                        name='AssetAmount'
                        placeholder={securityObject?.Ticker}
                    />
                    <Typography
                        className={classes.balanceText}
                        variant='h5'
                    >
                        {`Available ${securityObject?.Ticker} Balance: ${availableBalanceDisplay}`}
                    </Typography>
                    <Typography
                        className={classes.balanceText}
                        variant='h5'
                    >
                        {`Current Posted Amount: ${pledge.AssetAmount ?? '--'}`}
                    </Typography>
                </Grid>
            </Grid>
            <Grid
                className={classes.submitContainer}
                container
                direction='column'
                item
                justifyContent='center'
                spacing={0}
                xs={12}
            >
                <SubmitButton
                    disabled={isSubmitting || !isValid}
                    onClick={submitForm}
                    text='SUBMIT'
                    type='none'
                />
            </Grid>
            <Typography className={classes.legalText}>
                {'By pushing SUBMIT, I hereby certify that the information previously provided to Arca Institutional U.S. Treasury Fund in my Subscription Agreement (TFND), AML/KYC questionnaire and/or other documents provided to the TFND, as well as all account information, agreements and representations provided to tZERO Securities, LLC, remains accurate and complete in all respects as of the date hereof. Click here to access '}
                <a
                    href='https://storage.googleapis.com/lynq-docs/pdfs/arca_tfnd_ppm.pdf'
                    onClick={() => {
                        ReactGA.event('click', {
                            event_category: 'engagement',
                            event_label: 'tfnd_document_link'
                        });
                    }}
                    rel='noreferrer'
                    target='_blank'
                >
                    TFND&apos;s Private Placement Memorandum and Financial Statements.
                </a>
            </Typography>
        </form>
    );
};

export default function AddOnForm(props: IAddOnFormikProps) {
    const { formProps } = props;
    const { handleAddOn, assetPair, accountPositionBalance } = formProps;
    const securityAvailableBalance = accountPositionBalance?.[0]?.security[assetPair?.security?.AssetId ?? ''];

    const initialValues: IAddOnFormValues = {
        FiatAmount: '',
        AssetAmount: '',
        isFiatAmount: false
    };

    const validate = async (values: IAddOnFormValues) => {
        const errors: IAddOnFormErrors = {};
        await validateAddOn(values, {
            errors,
            securityAvailableBalance,
            assetPair
        });

        return errors;
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values: IAddOnFormValues) => {
                if (handleAddOn) {
                    await handleAddOn(values);
                }
            }}
            validate={validate}
            validateOnChange
            validateOnMount
        >
            {(formContentsProps) => (
                <FormContents
                    {...formContentsProps}
                    formProps={formProps}
                />
            )}
        </Formik>
    );
}